import { TopNavigation, TopNavigationProps } from '@amzn/awsui-components-core';
import { applyMode, Mode } from '@amzn/awsui-global-styles';
import { faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toggleTheme } from 'src/app/AppMetadataSlice';
import { checkTokenExpiration } from 'src/app/auth/TokenExpiry';
import { FINANCE_ADMIN } from 'src/constants/generic-constants';
import { topNavI18nStrings } from 'src/i18n-strings/top-nav-bar-i18nStrings';
import XPT_LOGO_W_NAME from 'src/images/xpt-logo-with-name.png';
import { RootState } from 'src/store/store';
import { navigateWithEmbedParams } from 'src/utils/navigation-utils';
import { useAuth } from '../../app/auth/AuthContextProvider';
import { UserImage } from '../../components/common/UserComponents';
import { ModifyUserAccess } from './ModifyUserAccess';
import { useNotificationBell } from 'src/features/notifications/useNotificationBell';
import { useNotificationsSubscription } from 'src/features/notifications/useNotificationsSubscription';
import { NotificationPanel } from 'src/features/notifications/NotificationPanel';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlagParameters } from 'src/utils/types/featureFlags';

const Visual_Mode_Options = [
  {
    id: 'dark',
    text: 'Dark',
    iconSvg: <FontAwesomeIcon icon={faMoon} />
  },
  {
    id: 'light',
    text: 'Light',
    iconSvg: <FontAwesomeIcon icon={faSun} />
  }
];

interface TokenExpirationTimes {
  idToken: string;
  accessToken: string;
}

export const XptTopNavigation: React.FC = () => {
  const { isInitiallyDev, isUserRoleModified, userDisplayName, userAlias } = useAuth();
  const isDarkModeEnabled = useSelector((state: RootState) => state.xptAppMetadataStore.themeMode === 'dark');
  const { currentUserAccessDetails } = useSelector((state: RootState) => state.xptAccessAndAuthorizationStore);
  const [expirationTimes, setExpirationTimes] = useState<TokenExpirationTimes | null>(null);
  const { isEnabled: isAccessRequestEnabled } = useFeatureFlag(FeatureFlagParameters.ACCESS_REQUEST_SELF_SERVICE);

  // Check if user is a Finance Admin for at least 1 Business Group.
  const isFinanceAdmin = currentUserAccessDetails?.user_data_classifications.some((classification) => classification.user_type === FINANCE_ADMIN);

  const [displayAuthModifierModal, setDisplayAuthModifierModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Notifications are gated behind a feature flag so the bell + its API calls (subscription + fetch)
  // are fully disabled in a stage where the AppSync notification API isn't deployed yet.
  const { isEnabled: notificationsEnabled } = useFeatureFlag(FeatureFlagParameters.NOTIFICATIONS);
  // Real-time notifications: subscribe + baseline load once the user is authenticated and notifications feature is enabled.
  useNotificationsSubscription(Boolean(userAlias) && notificationsEnabled);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const openNotificationPanel = useCallback(() => setShowNotificationPanel(true), []);
  const notificationBellUtility = useNotificationBell(openNotificationPanel);

  useEffect(() => {
    applyMode(isDarkModeEnabled ? Mode.Dark : Mode.Light);
  }, [isDarkModeEnabled]);

  const nonAdminUtilityItems = (): any[] => {
    if (!isAccessRequestEnabled) return [];
    return [
      { id: 'request-access', text: 'Request Access' },
      { id: 'my-requests', text: 'My Requests' }
    ];
  };

  const adminUtilityItems = (): any[] => {
    if (isFinanceAdmin) {
      return [
        {
          id: 'admin-dev-console',
          text: 'Finance Admin / Dev Console'
        }
      ];
    }
    return [];
  };

  useEffect(() => {
    const fetchExpirationTime = async () => {
      const times = await checkTokenExpiration();
      setExpirationTimes(times);
    };

    fetchExpirationTime();
    // const intervalId = setInterval(fetchExpirationTime, 1000);

    // return () => clearInterval(intervalId);
  }, []);

  const devTopNavBarUtility: TopNavigationProps.Utility[] = [
    // { type: 'button', text: `ID Token: ${expirationTimes?.idToken || 'Loading...'}` },
    // { type: 'button', text: `Access Token: ${expirationTimes?.accessToken || 'Loading...'}` },
    {
      type: 'button',
      text: `${isUserRoleModified ? 'Access Modified' : 'Modify Your Access'}`,
      iconName: 'security',
      variant: isUserRoleModified ? 'primary-button' : 'link',
      onFollow: () => {
        setDisplayAuthModifierModal(true);
      },
      onClick: () => {
        setDisplayAuthModifierModal(true);
      }
    }
  ];

  const onCancel = () => {
    setDisplayAuthModifierModal(false);
  };

  const onConfirm = () => {
    setDisplayAuthModifierModal(false);
    window.location.reload();
  };

  const profileMenuItems = adminUtilityItems().concat(nonAdminUtilityItems());

  const profileUtility: TopNavigationProps.Utility =
    profileMenuItems.length > 0
      ? {
          type: 'menu-dropdown',
          iconSvg: <UserImage alias={userAlias} marginRight="16px" />,
          text: userDisplayName,
          iconName: 'user-profile',
          items: profileMenuItems,
          onItemClick: ({ detail }) => handleUtilityItemClick(detail)
        }
      : {
          type: 'button',
          iconSvg: <UserImage alias={userAlias} marginRight="16px" />,
          text: userDisplayName,
          iconName: 'user-profile'
        };

  const topNavBarUtility: TopNavigationProps.Utility[] = [
    {
      type: 'menu-dropdown',
      iconSvg: isDarkModeEnabled ? <FontAwesomeIcon icon={faMoon} /> : <FontAwesomeIcon icon={faSun} />,
      description: 'Mode',
      onItemClick: (event) => onChangeVisualMode(event.detail.id === 'dark'),
      items: Visual_Mode_Options
    },
    ...(notificationsEnabled ? [notificationBellUtility] : []),
    profileUtility
  ];

  const handleUtilityItemClick = (detail: any) => {
    if (detail.id === 'admin-dev-console') {
      navigateWithEmbedParams(navigate, '/admin-console/planning-cycle');
    } else if (detail.id === 'request-access') {
      navigateWithEmbedParams(navigate, '/request-access');
    } else if (detail.id === 'my-requests') {
      navigateWithEmbedParams(navigate, '/my-requests');
    }
  };

  const onChangeVisualMode = (useDarkMode: boolean) => {
    dispatch(toggleTheme(useDarkMode));
  };

  const appTitleClicked = (e: any) => {
    e.preventDefault();
    navigateWithEmbedParams(navigate, '/');
  };

  return (
    <>
      <ModifyUserAccess showModal={displayAuthModifierModal} onCancel={onCancel} onConfirm={onConfirm} />
      {notificationsEnabled && <NotificationPanel visible={showNotificationPanel} onDismiss={() => setShowNotificationPanel(false)} />}
      <TopNavigation
        className="app-primary-top-navigation"
        i18nStrings={topNavI18nStrings}
        identity={{
          href: '/',
          // title: `${APP_NAME_SHORT}`,
          onFollow: appTitleClicked,
          logo: {
            src: XPT_LOGO_W_NAME,
            alt: 'xPT - Expense Planning Tool'
          }
        }}
        utilities={isInitiallyDev ? devTopNavBarUtility.concat(topNavBarUtility) : topNavBarUtility}
      />
    </>
  );
};
